import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight, Zap, AlertCircle, HelpCircle } from "lucide-react";
import { nationalFlag } from "@/lib/flags";
import ChipStatusPill from "@/components/onui/ChipStatusPill";
import { BentoModal } from "@/components/onui/BentoMotion";

/**
 * iter35d — Power Pick modal.
 * iter86.31.1 — Golf pick'em aware: the pick is a STROKES HANDICAP
 * (−N strokes per round the golfer actually plays), not a multiplier.
 * iter86.31.3 — Golf position draft aware: flat POINTS multiplier on
 * everything the picked golfer earns. Locks at R1 tee-off.
 * iter87.5 — Status pill in header + Chip Save Autoclose (parent
 * decides when to close, but a `status` prop paints the same pill the
 * chip carried).
 */
export default function PowerPickModal({
    open = true,
    eligibleTeams = [],
    ineligibleDrafted = [],
    noBonusDrafted = [],
    currentPickTeamId,
    multiplierDisplay,
    mode,
    flatMultiplier,
    quartilePreview,
    isLocked,
    isGolfPickem = false,
    isGolfDraft = false,
    isPotPickem = false,
    isSeason = false,
    potMultipliers = {},
    golfStrokes = 2,
    golfDraftMultiplier = 1.5,
    status,
    onPick,
    onClose,
}) {
    const hasNoEligible = eligibleTeams.length === 0;
    const isGolf = isGolfPickem || isGolfDraft;
    const [showExplainer, setShowExplainer] = useState(false);
    // iter86.42.2 — Pot pick'em: multiplier is priced by the club's pot.
    // iter87.306 (user-reported — "pot 4 power pick disappeared") — this
    // was hardcoded to only pots 1-3, so a 4-pot template (NFL Season
    // Pick'em's Moonshots tier) always rendered `undefined×` for pot 4
    // even though `potMultipliers` (from `state.power_pick_multipliers`)
    // carried the correct "4" key the whole time — display-only bug,
    // scoring itself read straight from that same backend map. Now
    // seeds defaults for 1-3 then overlays EVERY key actually present.
    const pm = { 1: 1, 2: 1.5, 3: 2 };
    for (const [k, v] of Object.entries(potMultipliers || {})) pm[Number(k)] = Number(v);
    // iter87.307 (operator-requested follow-up sweep) — every place that
    // lists individual pot tiers now derives the list from `pm`'s own
    // keys instead of a hand-written "1, 2, 3(, 4)" enumeration, so a
    // future 5th tier can't quietly go blank the same way pot 4 did.
    const potTiers = Object.keys(pm).map(Number).sort((a, b) => a - b);
    const POT_TIER_LABELS = { 1: "title contenders", 2: "chasing pack", 3: "rest of the table", 4: "deepest tier" };
    // iter87.264 (user-reported bug) — MLB/NBA/NFL-Playoffs/CFB-Playoffs pair
    // TWO seeds per pot; a straight seed clamp is wrong for those (fell back
    // to `t.pot`, computed correctly upstream in DashboardModalStack.jsx).
    // The clamp below is now ONLY a fallback for teams missing that field.
    const potForSeed = (t) => t?.pot ?? Math.min(Number(t?.seed) || 4, 3);

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="power-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="brutal-card p-4 sm:p-6 max-w-lg w-full max-h-[90dvh] overflow-y-auto border-volt"
        >
                <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="min-w-0 flex items-center gap-2 flex-1">
                        <div className="w-8 h-8 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                            <Zap className="w-4 h-4 text-black" strokeWidth={2.5} />
                        </div>
                        <h3 className="font-display font-extrabold text-lg uppercase truncate">Power Up</h3>
                        {status && <ChipStatusPill status={status} testId="power-pick-modal-status" />}
                    </div>
                    <button onClick={onClose} className="brutal-btn-ghost p-2 shrink-0" data-testid="close-power-modal-btn">
                        <ChevronRight className="w-4 h-4 rotate-90" />
                    </button>
                </div>
                {isGolfPickem ? (
                    <p className="text-[11px] text-zinc-400 leading-relaxed mb-2" data-testid="power-pick-golf-copy">
                        Pick <span className="text-volt font-bold">one of your golfers</span> — they get
                        <span className="text-volt font-bold"> {golfStrokes} stroke{golfStrokes > 1 ? "s" : ""} shaved off every round they actually play</span> (miss
                        cut = −{golfStrokes * 2}, make cut = −{golfStrokes * 4}). Only counts on YOUR card.
                    </p>
                ) : isGolfDraft ? (
                    <p className="text-[11px] text-zinc-400 leading-relaxed mb-2" data-testid="power-pick-golf-draft-copy">
                        Pick <span className="text-volt font-bold">one of your drafted golfers</span> — every point
                        they earn (make cut + finish position) is multiplied by
                        <span className="text-volt font-bold"> {golfDraftMultiplier}×</span>. Only counts on YOUR card.
                    </p>
                ) : isPotPickem ? (
                    <p className="text-[11px] text-zinc-400 leading-relaxed mb-2" data-testid="power-pick-pot-copy">
                        Pick <span className="text-volt font-bold">one of your clubs</span> — every W/D/L point they earn all season is multiplied by
                        <span className="text-volt font-bold"> its pot</span> ({potTiers.map(n => `Pot ${n} = ${pm[n]}×`).join(" · ")}). The deeper the pot, the bigger the boost.
                    </p>
                ) : isSeason ? (
                    <p className="text-[11px] text-zinc-400 leading-relaxed mb-2" data-testid="power-pick-season-copy">
                        Pick <span className="text-volt font-bold">one of your drafted clubs</span>
                        {multiplierDisplay && (
                            <> — they earn <span className="text-volt font-bold">{multiplierDisplay}</span> on every W/D/L point all season</>
                        )}.
                    </p>
                ) : (
                    <p className="text-[11px] text-zinc-400 leading-relaxed mb-2">
                        Pick <span className="text-volt font-bold">one of your drafted teams</span> still alive
                        {multiplierDisplay && (
                            <> — they earn <span className="text-volt font-bold">{multiplierDisplay}</span> knockout points</>
                        )}.
                    </p>
                )}

                {!isGolf && quartilePreview && (
                    <div
                        className="brutal-card-light p-2 mb-2 border-volt/40 flex items-center justify-between gap-2"
                        data-testid="power-pick-quartile-preview"
                    >
                        <div className="text-[10px] text-zinc-400 uppercase tracking-widest font-bold">
                            {isLocked ? "Your multiplier" : "Current standing"}
                        </div>
                        <div className="text-[11px] text-white font-bold">
                            {quartilePreview.label} → <span className="text-volt">{quartilePreview.mult}</span>
                        </div>
                    </div>
                )}
                {!isGolf && quartilePreview && !isLocked && (
                    <p className="text-[10px] text-zinc-500 italic mb-2 -mt-1">
                        Preview only — your final multiplier locks when the first knockout round starts.
                    </p>
                )}
                <p className="text-[10px] text-zinc-500 italic mb-2 flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    {isGolf
                        ? "Locks at Round 1 tee-off. Change it as often as you like until then."
                        : isPotPickem || isSeason
                        ? "Locks at first kickoff. Change it as often as you like until then."
                        : "Locks once the first knockout round starts. Eliminated teams aren't eligible."}
                </p>

                {!isGolf && (
                    <button
                        type="button"
                        onClick={() => setShowExplainer(v => !v)}
                        className="text-[10px] text-volt/80 hover:text-volt underline decoration-dotted underline-offset-2 uppercase tracking-wider font-bold flex items-center gap-1 mb-4"
                        data-testid="power-pick-explainer-toggle"
                    >
                        <HelpCircle className="w-3 h-3" />
                        {showExplainer ? "Hide explainer" : "Why these multipliers?"}
                    </button>
                )}

                {!isGolf && showExplainer && (
                    <div
                        className="brutal-card-light p-3 mb-4 border-volt/30 text-[11px] text-zinc-300 leading-relaxed"
                        data-testid="power-pick-explainer"
                    >
                        {mode === "pot" || isPotPickem ? (
                            <>
                                <p className="mb-1">
                                    <span className="text-volt font-bold uppercase tracking-wider">Pot mode.</span> The multiplier is set by which pot your nominated club came from — the deeper the pot, the bigger the reward:
                                </p>
                                <ul className="space-y-0.5 pl-3 list-disc marker:text-volt/70">
                                    {potTiers.map(n => (
                                        <li key={n}>Pot {n} ({POT_TIER_LABELS[n] || "extra tier"}) → <span className="text-volt font-bold">{pm[n]}×</span></li>
                                    ))}
                                </ul>
                                <p className="text-[10px] text-zinc-500 italic mt-1">A longshot that racks up wins pays double — that's the gamble.</p>
                            </>
                        ) : mode === "handicap" ? (
                            <>
                                <p className="mb-1">
                                    <span className="text-volt font-bold uppercase tracking-wider">Handicap mode.</span> Multipliers scale with your leaderboard rank so trailing crewmates can still claw back.
                                </p>
                                <ul className="space-y-0.5 pl-3 list-disc marker:text-volt/70">
                                    <li>Bottom 25% (laggards) → <span className="text-volt font-bold">2×</span></li>
                                    <li>Middle 50% → <span className="text-volt font-bold">1.75×</span></li>
                                    <li>Top 25% (leaders) → <span className="text-volt font-bold">1.5×</span></li>
                                </ul>
                                <p className="text-[10px] text-zinc-500 italic mt-1">Rivalries with 4 or fewer crewmates skip the quartile — everyone gets 1.75×.</p>
                            </>
                        ) : (
                            <>
                                <p className="mb-1">
                                    <span className="text-volt font-bold uppercase tracking-wider">Flat mode.</span> Your admin set a single multiplier that applies to every crewmate's Power Pick.
                                </p>
                                <p>
                                    Everyone earns <span className="text-volt font-bold">{flatMultiplier ? `${flatMultiplier}×` : "the same boost"}</span> on their nominated team's {isSeason ? "season W/D/L" : "knockout"} points.
                                </p>
                            </>
                        )}
                    </div>
                )}

                {hasNoEligible ? (
                    <div className="brutal-card-light p-4 text-center border-blaze/40">
                        <p className="text-sm text-zinc-400">
                            {isGolfPickem
                                ? "Lock in your golfer picks first — then you can nominate one for the handicap."
                                : isGolfDraft
                                ? "You don't have any golfers yet — Power Pick opens once the draft assigns your golfers."
                                : isPotPickem && noBonusDrafted.length > 0
                                ? "All your clubs are in Pot 1 (no multiplier bonus) — nothing worth power picking this season."
                                : isPotPickem
                                ? "Lock in your club picks first — then you can nominate one for the pot multiplier."
                                : "You don't have any drafted teams still alive — Power Pick isn't available for you this tournament."}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-2 mb-3">
                            {eligibleTeams.map(t => (
                                <motion.button
                                    key={t.id}
                                    data-testid={`power-team-${t.id}`}
                                    onClick={() => onPick(t.id)}
                                    whileHover={{ y: -2, scale: 1.015 }}
                                    whileTap={{ scale: 0.97 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                    className={`p-3 border-2 rounded-md text-left ${
                                        currentPickTeamId === t.id
                                            ? "border-volt bg-volt/20 shadow-brutal"
                                            : "border-[#333] bg-ink hover:border-volt"
                                    }`}
                                >
                                    {nationalFlag(t.flag) && <div className="text-xl mb-1">{nationalFlag(t.flag)}</div>}
                                    <div className="text-sm font-bold truncate">{t.name}</div>
                                    {isPotPickem && (
                                        <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-0.5">
                                            Pot {potForSeed(t)} · <span className="text-volt font-bold">{pm[potForSeed(t)]}×</span>
                                        </div>
                                    )}
                                    {currentPickTeamId === t.id && (
                                        <div className="text-[9px] text-volt uppercase tracking-widest mt-1 font-bold flex items-center gap-1">
                                            <Zap className="w-2.5 h-2.5" /> Power Pick
                                        </div>
                                    )}
                                </motion.button>
                            ))}
                        </div>
                        {ineligibleDrafted.length > 0 && (
                            <div className="border-t-2 border-[#222] pt-3 mt-3">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2 font-bold">Eliminated / missed the field (not eligible)</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {ineligibleDrafted.map(t => (
                                        <div
                                            key={t.id}
                                            className="p-3 border-2 border-[#222] rounded-md bg-ink/40 opacity-50 cursor-not-allowed"
                                            data-testid={`power-team-disabled-${t.id}`}
                                        >
                                            {nationalFlag(t.flag) && <div className="text-xl mb-1 grayscale">{nationalFlag(t.flag)}</div>}
                                            <div className="text-sm font-bold truncate line-through">{t.name}</div>
                                            {t.missed_playoffs && !t.eliminated && (
                                                <div className="text-[9px] text-amber-500 uppercase tracking-wider mt-0.5">Missed the field</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {/* iter87.212.32 — Pot 1 clubs are still alive, just
                            not worth power-picking (no multiplier bonus).
                            Separate from "Eliminated" — no strikethrough,
                            and the pot badge stays so it's clear WHY. */}
                        {noBonusDrafted.length > 0 && (
                            <div className="border-t-2 border-[#222] pt-3 mt-3">
                                <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2 font-bold">Pot 1 · No multiplier (not eligible)</p>
                                <div className="grid grid-cols-2 gap-2">
                                    {noBonusDrafted.map(t => (
                                        <div
                                            key={t.id}
                                            className="p-3 border-2 border-[#222] rounded-md bg-ink/40 opacity-50 cursor-not-allowed"
                                            data-testid={`power-team-no-bonus-${t.id}`}
                                        >
                                            {nationalFlag(t.flag) && <div className="text-xl mb-1 grayscale">{nationalFlag(t.flag)}</div>}
                                            <div className="text-sm font-bold truncate">{t.name}</div>
                                            <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-0.5">
                                                Pot {potForSeed(t)} · {pm[potForSeed(t)]}× (no bonus)
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
        </BentoModal>
    );
}
